import React from 'react';
import {
  MapPin,
  Euro,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  Star,
  BarChart3,
  Target,
  Lightbulb,
  Sparkles,
  Award,
  Zap,
  Shield,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import MapboxMap from './MapboxMap';

// Listing Intelligence Components
import { ContentGenerator, AuditReport, MarketContext } from './listings';

// Number formatters
import { formatNumber, formatCurrency, formatArea, formatPricePerM2, formatRatio } from '../utils/formatters';

// Phase 2 Components
import OverallVerdictCard from './OverallVerdictCard';
import AgentInsightsSection from './AgentInsightsSection';

// Phase 1 GeoCLIP Components
import ConfidenceBreakdownCard from './ConfidenceBreakdownCard';
import RegionalClassificationCard from './RegionalClassificationCard';
import PropertyFeaturesCard from './PropertyFeaturesCard';

// Phase 2 VPR Components
import ContributeToVPRButton from './ContributeToVPRButton';
import VPRRefinementBadge from './VPRRefinementBadge';

// ProprHome logo component with distinctive house icon
const ProprHomeIcon = ({ className = "w-6 h-6" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* ProprHome house icon with distinctive extended base/tag shape */}
    <path d="M12 2L3 7v10c0 1.1.9 2 2 2h6v-4h2v4h6c1.1 0 2-.9 2-2V7l-9-5z" />
    <path d="M8 10h8v6H8v-6z" />
    <path d="M10 12h4v2h-4v-2z" />
    {/* Extended base/tag shape on the right side */}
    <path d="M16 12h4v4h-4v-4z" />
    <path d="M18 14h2v2h-2v-2z" />
  </svg>
);

/**
 * PropertyResults component to display analysis results
 * @param {Object} props - Component props
 * @param {Object} props.result - Analysis result data
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
// Helper function to get score color and icon
const getScoreDisplay = (score) => {
  if (score >= 80) return { color: 'text-primary-600', bg: 'bg-primary-50', icon: ArrowUp, trend: 'Excellent' };
  if (score >= 60) return { color: 'text-turquoise-600', bg: 'bg-turquoise-50', icon: ArrowUp, trend: 'Good' };
  if (score >= 40) return { color: 'text-yellow-600', bg: 'bg-yellow-50', icon: Minus, trend: 'Average' };
  return { color: 'text-red-600', bg: 'bg-red-50', icon: ArrowDown, trend: 'Poor' };
};

// Helper function to get overall score display
const getOverallScoreDisplay = (score) => {
  if (score >= 80) return {
    color: 'text-primary-600',
    bg: 'from-primary-500 to-primary-600',
    icon: Award,
    label: 'Excellent Investment',
    description: 'This property shows strong potential'
  };
  if (score >= 60) return {
    color: 'text-turquoise-600',
    bg: 'from-turquoise-500 to-turquoise-600',
    icon: Shield,
    label: 'Good Investment',
    description: 'This property has solid fundamentals'
  };
  if (score >= 40) return {
    color: 'text-yellow-600',
    bg: 'from-yellow-500 to-yellow-600',
    icon: Zap,
    label: 'Average Investment',
    description: 'This property has mixed potential'
  };
  return {
    color: 'text-red-600',
    bg: 'from-red-500 to-red-600',
    icon: AlertTriangle,
    label: 'Risky Investment',
    description: 'This property has significant concerns'
  };
};

/**
 * Get score category from score value
 */
function getScoreCategory(score) {
  if (score >= 85) return 'Excellent Investment';
  if (score >= 70) return 'Good Investment';
  if (score >= 55) return 'Fair Opportunity';
  if (score >= 40) return 'Average Listing';
  return 'Needs Investigation';
}

/**
 * Generate key insight based on analysis
 */
function getKeyInsight(result) {
  const { priceEfficiency, locationContext, listingQuality } = result;

  // Generate insight based on strongest factor
  if (locationContext?.score >= 75) {
    return 'Prime location with strong market fundamentals. Good positioning for both rental income and capital appreciation.';
  } else if (priceEfficiency?.pricePerM2 && priceEfficiency.pricePerM2 < 2500) {
    return 'Well-priced property offering good value for the area. Competitive pricing makes this attractive for buyers.';
  } else if (listingQuality?.score >= 70) {
    return 'Well-presented listing with comprehensive information. Ready for client viewing and comparison.';
  } else {
    return 'Standard market listing. Consider requesting additional information and comparable properties for validation.';
  }
}

export default function PropertyResults({ result, loading, error }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-md mx-auto px-4">
          {/* Animated Spinner */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-t-emerald-600 rounded-full animate-spin"></div>
            <Sparkles className="absolute inset-0 m-auto w-10 h-10 text-emerald-600 animate-pulse" />
          </div>

          {/* Loading Message */}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Analyzing Property...
          </h3>
          <p className="text-gray-600 mb-4">
            Our AI is processing the listing data
          </p>

          {/* Progress Steps */}
          <div className="space-y-2 text-sm text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <div className="w2 h-2 rounded-full bg-purple-600 animate-pulse"></div>
              <span>Scraping property details</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse animation-delay-200"></div>
              <span>Analyzing with GeoCLIP</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-300 animate-pulse animation-delay-400"></div>
              <span>Generating insights</span>
            </div>
          </div>

          {/* Time Estimate */}
          <p className="text-xs text-gray-400 mt-6">
            This usually takes 5-10 seconds
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>

            <div className="flex-1">
              {/* Error Title */}
              <h3 className="text-xl font-bold text-red-900 mb-2">
                Analysis Failed
              </h3>

              {/* Error Message */}
              <p className="text-red-700 mb-4">
                {error || 'An unexpected error occurred while analyzing the property.'}
              </p>

              {/* Actionable Suggestions */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Try these solutions:
                </p>
                <ul className="space-y-2 text-sm text-red-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Verify the URL is from <strong>Imovirtual.com</strong> or <strong>Idealista.pt</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Check if the listing is still active and published</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Make sure the URL is complete (includes https://)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Try a different property from the same portal</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 shadow-sm"
                >
                  <Zap className="w-4 h-4" />
                  Try Again
                </button>

                <button
                  onClick={() => window.history.back()}
                  className="px-6 py-3 bg-white text-red-900 font-medium rounded-lg border-2 border-red-200 hover:bg-red-50 transition-colors"
                >
                  Go Back
                </button>

                <a
                  href="/dashboard"
                  className="px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border-2 border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Return to Dashboard
                </a>
              </div>

              {/* Support Link */}
              <p className="text-sm text-gray-600 mt-6">
                Still having issues?{' '}
                <a
                  href="mailto:support@proprscout.com"
                  className="text-purple-600 hover:text-purple-700 font-medium underline"
                >
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { propertyData, analysis } = result;
  // Support both old and new structure for backward compatibility
  const priceEfficiency = analysis.priceEfficiency || analysis.priceAnalysis;
  const locationContext = analysis.locationContext || analysis.locationAnalysis;
  const spaceConfiguration = analysis.spaceConfiguration || analysis.propertyAnalysis;
  const { overallScore, dataQuality, listingQuality, recommendations, risks, opportunities, disclaimer, aiAnalysis } = analysis;
  const overallDisplay = getOverallScoreDisplay(overallScore.score);

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-6 py-6">
      {/* Demo Data Banner */}
      {propertyData.isDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 animate-fade-in">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Demo Mode</h3>
              <p className="text-sm text-amber-700 mt-1">
                This analysis uses realistic demo data for demonstration purposes. For real property analysis, use actual property URLs from Portuguese real estate websites.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Verdict Card - PRIMARY ASSESSMENT */}
      <OverallVerdictCard
        score={overallScore.score}
        category={getScoreCategory(overallScore.score)}
        keyInsight={getKeyInsight(analysis)}
        result={result}
      />

      {/* Agent Insights Section - ACTIONABLE INTELLIGENCE */}
      <AgentInsightsSection result={analysis} />

      {/* Phase 1 GeoCLIP Enhancements - PORTUGUESE REGIONAL INTELLIGENCE */}
      {result.geolocation && (
        <div className="space-y-6">
          {/* Portuguese Regional Classification */}
          {result.geolocation.portugueseRegion && (
            <RegionalClassificationCard
              portugueseRegion={result.geolocation.portugueseRegion}
            />
          )}

          {/* Enhanced Confidence Breakdown */}
          {result.geolocation.enhancedConfidence && (
            <ConfidenceBreakdownCard
              enhancedConfidence={result.geolocation.enhancedConfidence}
            />
          )}

          {/* Multi-Dimensional Property Features */}
          {result.geolocation.features && (
            <PropertyFeaturesCard
              features={result.geolocation.features}
            />
          )}

          {/* Phase 2 VPR Refinement Badge */}
          {(result.geolocation?.vprRefinement || result.geolocation?.highPrecisionArea) && (
            <VPRRefinementBadge
              vprRefinement={result.geolocation.vprRefinement}
              highPrecisionArea={result.geolocation.highPrecisionArea}
            />
          )}
        </div>
      )}

      {/* Phase 2 VPR - Contribute to Database */}
      {result.geolocation?.coordinates && (
        <ContributeToVPRButton
          coordinates={{
            lat: result.geolocation.coordinates.latitude,
            lon: result.geolocation.coordinates.longitude
          }}
          propertyUrl={result.url}
        />
      )}

      {/* Disclaimer Banner */}
      {disclaimer && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 animate-fade-in">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-800">{disclaimer}</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Quality Warnings */}
      {dataQuality && (dataQuality.issues.length > 0 || dataQuality.warnings.length > 0) && (
        <div className={`border-l-4 rounded-lg p-4 ${dataQuality.hasCriticalIssues ? 'bg-red-50 border-red-400' : 'bg-yellow-50 border-yellow-400'}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${dataQuality.hasCriticalIssues ? 'text-red-400' : 'text-yellow-400'}`} />
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium mb-2 ${dataQuality.hasCriticalIssues ? 'text-red-800' : 'text-yellow-800'}`}>
                {dataQuality.hasCriticalIssues ? 'Critical Data Issues' : 'Data Quality Warnings'}
              </h3>
              {dataQuality.issues.length > 0 && (
                <ul className="list-disc list-inside space-y-1 mb-2">
                  {dataQuality.issues.map((issue, idx) => (
                    <li key={idx} className="text-sm text-red-700">{issue}</li>
                  ))}
                </ul>
              )}
              {dataQuality.warnings.length > 0 && (
                <ul className="list-disc list-inside space-y-1">
                  {dataQuality.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-700">{warning}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agent Insights Section - ACTIONABLE INTELLIGENCE */}
      <AgentInsightsSection result={analysis} />

      {/* Premium Score Cards - Compact Single Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Price Efficiency Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center">
                <Euro className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 font-heading">Price Efficiency</h3>
                <p className="text-sm text-gray-600">Calculated metrics</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-primary-600">{priceEfficiency.score}</div>
              <div className="text-sm text-gray-600">/100</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Efficiency Score</span>
              <span>{priceEfficiency.score}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-primary-400 to-primary-600 h-3 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${priceEfficiency.score}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-4">
            {priceEfficiency.explanation && (
              <div className="p-4 bg-primary-50 rounded-xl">
                <div className="text-xs text-primary-700">{priceEfficiency.explanation}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {priceEfficiency.pricePerM2 && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">{formatPricePerM2(priceEfficiency.pricePerM2)}</div>
                  <div className="text-xs text-gray-600">Price/m²</div>
                </div>
              )}
              {priceEfficiency.spaceEfficiency && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">{formatArea(priceEfficiency.spaceEfficiency)}</div>
                  <div className="text-xs text-gray-600">Space/Room</div>
                </div>
              )}
              {priceEfficiency.pricePerRoom && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-lg font-bold text-gray-900">{priceEfficiency.pricePerRoom.toLocaleString()}€</div>
                  <div className="text-xs text-gray-600">Price/Room</div>
                </div>
              )}
              {priceEfficiency.disclaimer && (
                <div className="col-span-2 p-3 bg-amber-50 rounded-lg">
                  <div className="text-xs text-amber-700">{priceEfficiency.disclaimer}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Location Context Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all duration-300 hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Location Context</h3>
                <p className="text-sm text-gray-600">Area information</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-blue-600">{locationContext.score}</div>
              <div className="text-sm text-gray-600">/100</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Location Score</span>
              <span>{locationContext.score}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${locationContext.score}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <div className="text-sm font-semibold text-blue-800 mb-1">{locationContext.neighborhood}</div>
              <div className="text-xs text-blue-600">Area context</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-900">{locationContext.transportScore}</div>
                <div className="text-xs text-gray-600">Transport</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-900">{locationContext.amenities.length}</div>
                <div className="text-xs text-gray-600">Amenities</div>
              </div>
            </div>
            {locationContext.explanation && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-700">{locationContext.explanation}</div>
              </div>
            )}
          </div>
        </div>

        {/* Space Configuration Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all duration-300 hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                <ProprHomeIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Space Configuration</h3>
                <p className="text-sm text-gray-600">Layout analysis</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-purple-600">{spaceConfiguration.score}</div>
              <div className="text-sm text-gray-600">/100</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Configuration Score</span>
              <span>{spaceConfiguration.score}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-purple-400 to-purple-600 h-3 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${spaceConfiguration.score}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-4">
            {spaceConfiguration.areaPerRoom && (
              <div className="p-4 bg-purple-50 rounded-xl">
                <div className="text-sm font-semibold text-purple-800 mb-1">Area per Room</div>
                <div className="text-lg font-bold text-purple-600">{formatArea(spaceConfiguration.areaPerRoom)}</div>
                {spaceConfiguration.spaceEfficiency && (
                  <div className="text-xs text-purple-600 mt-1 capitalize">{spaceConfiguration.spaceEfficiency} space</div>
                )}
              </div>
            )}

            {spaceConfiguration.bathroomRatio && (
              <div className="p-4 bg-purple-50 rounded-xl">
                <div className="text-sm font-semibold text-purple-800 mb-1">Bathroom Ratio</div>
                <div className="text-lg font-bold text-purple-600">{formatRatio(spaceConfiguration.bathroomRatio)}</div>
                {spaceConfiguration.bathroomAssessment && (
                  <div className="text-xs text-purple-600 mt-1 capitalize">{spaceConfiguration.bathroomAssessment} ratio</div>
                )}
              </div>
            )}

            {spaceConfiguration.featureCompleteness && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-700 mb-1">Features: {spaceConfiguration.featureCompleteness.total} total</div>
                <div className="text-xs text-gray-600">
                  {spaceConfiguration.featureCompleteness.premium > 0 && `${spaceConfiguration.featureCompleteness.premium} premium`}
                  {spaceConfiguration.featureCompleteness.premium > 0 && spaceConfiguration.featureCompleteness.modern > 0 && ', '}
                  {spaceConfiguration.featureCompleteness.modern > 0 && `${spaceConfiguration.featureCompleteness.modern} modern`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
